import React, { useState } from "react";
import sortBy from "lodash/sortBy";
import { Link, useNavigate, useParams } from "react-router-dom";

import PageContent from "ui/PageContent";
import Panel from "ui/Panel";
import Loading from "ui/Loading";

import { useRoutes } from "utils/routes";
import { MemberEditDocument, useMemberEditQuery } from "./gql/Query.generated";
import { useUpdateOrganizationMembershipMutation } from "./gql/Mutation.generated";
import { useSetFlash, useSetNextFlashLocation } from "providers/WithFlashes";
import MultiSelect from "ui/MultiSelect";
import { MemberDetailsDocument } from "features/Organization/MemberDetails/gql/Query.generated";

type RoleOptionType = {
  value: string;
  label: string;
};

const MemberEdit = () => {
  const { slug: organizationSlug, id: membershipId } = useParams();
  const { data, loading, error } = useMemberEditQuery({
    variables: { organizationSlug, membershipId },
  });
  if (loading || error) {
    return <Loading error={!!error} />;
  }

  const member = data.getOrganizationMembershipDetails;
  const sortedRoles = sortBy(data.getOrganizationRoles, (k) => k.updatedAt);
  const currentRoles = member.roles.map((r) => {
    const role = sortedRoles.find((sr) => sr.id == r.id);
    return {
      value: role.id,
      label: role.name,
    };
  });
  const roleOptions = sortedRoles.map((sr) => {
    return {
      value: sr.id,
      label: sr.name,
    };
  });

  return (
    <PageContent
      title={`Organization Member: ${
        (member.accepted && member.user.fullname) || member.inviteEmail
      }`}
      pageCategory="organization-members"
      pageName="edit"
    >
      <MemberEditContent
        organizationSlug={organizationSlug}
        membershipId={membershipId}
        currentRoles={currentRoles}
        roleOptions={roleOptions}
      />
    </PageContent>
  );
};

const MemberEditContent = ({
  organizationSlug,
  membershipId,
  currentRoles,
  roleOptions,
}: {
  organizationSlug: string;
  membershipId: string;
  currentRoles: RoleOptionType[];
  roleOptions: RoleOptionType[];
}) => {
  const [roles, setRoles] = useState(currentRoles);
  const setFlash = useSetFlash();
  const setNextFlashLocation = useSetNextFlashLocation();
  const [updateOrganizationMembership] =
    useUpdateOrganizationMembershipMutation();

  const { organizationMember, organizationRoles } = useRoutes();
  const navigate = useNavigate();

  const updateMember = () => {
    if (roles.length === 0) {
      setFlash(
        "error",
        "You must select at least one role to assign to the member.",
      );
      return;
    }
    updateOrganizationMembership({
      variables: {
        membershipId: membershipId,
        roleIds: roles.map((r) => r.value),
      },
      onError: (error) => {
        setFlash("error", `Failed to update assigned roles: ${error.message}`);
      },
      refetchQueries: [
        {
          query: MemberDetailsDocument,
          variables: { membershipId },
        },
        {
          query: MemberEditDocument,
          variables: { organizationSlug, membershipId },
        },
      ],
      awaitRefetchQueries: true,
      onCompleted: (data) => {
        setFlash(
          "success",
          `Updated assigned roles for ${data.updateOrganizationMembership.email}`,
        );
        setNextFlashLocation(
          organizationMember(organizationSlug, membershipId),
        );
        navigate(organizationMember(organizationSlug, membershipId));
      },
    });
  };

  return (
    <Panel title="Edit Membership">
      <div className="p-[10px] max-w-[600px]">
        <div className="mb-2">
          <strong>Assigned Roles</strong>
        </div>
        <div className="row mb-2">
          <div className="col-sm-6">
            <MultiSelect
              value={roles}
              options={roleOptions}
              onChange={(newRoles) => setRoles(newRoles)}
              isClearable={false}
            />
          </div>
          <div className="help-block col-sm-6">
            Select one or multiple roles to apply to this member. Each role may
            contain multiple permissions, check the{" "}
            <Link to={organizationRoles(organizationSlug)}>Roles page</Link> for
            details.
          </div>
        </div>
        <div>
          <button className="btn btn-success" onClick={updateMember}>
            Save
          </button>
        </div>
      </div>
    </Panel>
  );
};

export default MemberEdit;
